import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './Auth0Context';

// Create context
const AppContext = createContext();

// Custom hook to use the context
export const useAppContext = () => useContext(AppContext);

// Provider component
export const AppProvider = ({ children }) => {
  // Get current user from auth context
  const { user, isAdmin, tokenReady } = useAuth();

  // State for projects
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for users
  const [users, setUsers] = useState([]);

  // State for project-user assignments
  const [projectUsers, setProjectUsers] = useState([]);

  // State for tasks
  const [tasks, setTasks] = useState([]);

  // State for timesheets (actual hours)
  const [timesheets, setTimesheets] = useState([]);

  // Dashboard summary data
  const [dashboardData, setDashboardData] = useState({
    totalProjects: 0,
    teamMembers: 0,
    plannedHours: 0,
    actualHours: 0,
    costVariance: 0,
    utilization: 0,
    wpsCompletionRate: 0,
    totalInvoiced: 0,
    totalOverhead: 0,
    activeProjectStages: {},
    projects: [],
    users: [],
    alerts: [],
    recentActivity: []
  });

  // Update dashboard data based on current state
  const updateDashboardData = useCallback((
    projectsData = projects,
    usersData = users, 
    projectUsersData = projectUsers,
    tasksData = tasks,
    actualHoursData = [],
    plannedHoursData = []
  ) => {
    if (!projectsData || !usersData) return;

    console.log('Updating dashboard data with:', {
      projects: projectsData.length,
      users: usersData.length,
      projectUsers: projectUsersData?.length,
      plannedHours: plannedHoursData?.length
    });

    // Filter data based on user role and access
    const currentUserIsAdmin = isAdmin();

    // Debug logging for user and admin status
    console.log('🔍 AppContext - updateDashboardData DEBUG:');
    console.log('  - Current user:', user);
    console.log('  - User ID:', user?.id);
    console.log('  - User role:', user?.role);
    console.log('  - Employee ID:', user?.employee_id);
    console.log('  - Is admin?:', currentUserIsAdmin);
    console.log('  - Raw projects count:', projectsData.length);
    console.log('  - Raw projects:', projectsData);
    console.log('  - Project users assignments:', projectUsersData);

    // For regular users, filter data to only show their assigned projects
    let filteredProjectsData = projectsData;
    let filteredUsersData = usersData;
    let filteredPlannedHoursData = plannedHoursData;
    let filteredActualHoursData = actualHoursData;

    if (!currentUserIsAdmin && user) {
      // Get user's assigned project IDs
      // Use employee_id (database user ID) instead of Auth0 user ID
      const userDatabaseId = user.employee_id || user.id;
      const userProjectIds = projectUsersData
        .filter(pu => pu.user_id === userDatabaseId)
        .map(pu => pu.project_id);

      console.log('  - User project assignments for database user ID', userDatabaseId, ':', userProjectIds);

      // Filter projects to only user's assigned projects
      filteredProjectsData = projectsData.filter(project =>
        userProjectIds.includes(project.id)
      );

      console.log('  - Filtered projects for regular user:', filteredProjectsData);

      // Filter planned hours to only user's data
      filteredPlannedHoursData = plannedHoursData.filter(entry =>
        entry.user_id === userDatabaseId
      );

      // Filter actual hours to only user's data
      filteredActualHoursData = actualHoursData.filter(entry =>
        entry.user_id === userDatabaseId
      );

      // For regular users, only show themselves in user data
      filteredUsersData = usersData.filter(u => u.id === userDatabaseId);
    } else {
      console.log('  - Admin user - showing all projects:', filteredProjectsData);
    }

    // Calculate total planned and actual hours from filtered data
    let totalPlannedHours = filteredPlannedHoursData.reduce((sum, entry) => sum + (entry.hours || 0), 0);
    let totalActualHours = filteredActualHoursData.reduce((sum, entry) => sum + (entry.hours || 0), 0);

    // Calculate financial metrics
    let totalInvoiced = projectsData.reduce((sum, project) => sum + (project.invoiced_amount || 0), 0);
    let totalOverhead = projectsData.reduce((sum, project) => {
      const projectCost = (project.hourly_rate || 0) * totalActualHours;
      return sum + (projectCost * (project.overhead_percentage || 0) / 100);
    }, 0);

    // Calculate WPS completion rate using filtered data
    const completedWPS = filteredPlannedHoursData.filter(entry => entry.status === 'completed').length;
    const wpsCompletionRate = filteredPlannedHoursData.length > 0 ? 
      Math.round((completedWPS / filteredPlannedHoursData.length) * 100) : 0;

    // Calculate project stages distribution using filtered data
    const activeProjectStages = filteredProjectsData.reduce((acc, project) => {
      const stage = project.project_stage_id || 'unassigned';
      acc[stage] = (acc[stage] || 0) + 1;
      return acc;
    }, {});

    // Filter projects to show only those with recent WPS activity (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Get project IDs that have recent WPS activity
    const recentWPSProjectIds = new Set();
    if (plannedHoursData && plannedHoursData.length > 0) {
      plannedHoursData.forEach(entry => {
        // Check if entry was created or updated in the last 30 days
        const createdAt = new Date(entry.created_at);
        const updatedAt = entry.updated_at ? new Date(entry.updated_at) : createdAt;
        const mostRecentDate = updatedAt > createdAt ? updatedAt : createdAt;

        if (mostRecentDate >= thirtyDaysAgo) {
          recentWPSProjectIds.add(entry.project_id);
        }
      });
    }

    // Filter projects to only show those with recent WPS activity, limited to 10 projects
    const recentActiveProjects = filteredProjectsData
      .filter(project => recentWPSProjectIds.has(project.id))
      .slice(0, 10); // Limit to maximum 10 projects to prevent overcrowding

    // Process projects for dashboard using filtered data (only recent active projects)
    const dashboardProjects = recentActiveProjects.map(project => {
      // Find all assignments for this project
      const projectAssignments = projectUsersData.filter(pu => pu.project_id === project.id);

      // Calculate planned and actual hours for this project
      const planned_hours = projectAssignments.length * 40; // Simplified calculation
      // Use project ID as seed for consistent "random" values
      const seed = project.id * 0.1;
      const randomFactor = 0.85 + (seed % 1) * 0.3;
      const actual_hours = Math.floor(planned_hours * randomFactor);

      totalPlannedHours += planned_hours;
      totalActualHours += actual_hours;

      return {
        ...project,
        planned_hours,
        actual_hours,
        team_size: projectAssignments.length
      };
    });

    // Process users for dashboard using filtered data
    const dashboardUsers = filteredUsersData.map(user => {
      // Find all assignments for this user
      const userAssignments = projectUsersData.filter(pu => pu.user_id === user.id);

      // Calculate planned and actual hours for this user
      const planned_hours = userAssignments.length * 40; // Simplified calculation
      // Use user ID as seed for consistent "random" values
      const seed = user.id * 0.1;
      const randomFactor = 0.85 + (seed % 1) * 0.3;
      const actual_hours = Math.floor(planned_hours * randomFactor);

      return {
        ...user,
        planned_hours,
        actual_hours,
        project_count: userAssignments.length
      };
    });

    // Calculate cost variance (simplified) using filtered data
    const avgHourlyRate = filteredProjectsData.reduce((sum, project) => sum + project.hourly_rate, 0) /
                          (filteredProjectsData.length || 1);
    const costVariance = (totalPlannedHours - totalActualHours) * avgHourlyRate;

    // Calculate utilization
    const utilization = (totalActualHours / (totalPlannedHours || 1)) * 100;

    setDashboardData({
      totalProjects: filteredProjectsData.length,
      teamMembers: filteredUsersData.length,
      plannedHours: totalPlannedHours,
      actualHours: totalActualHours,
      costVariance,
      utilization,
      wpsCompletionRate,
      totalInvoiced,
      totalOverhead,
      activeProjectStages,
      projects: dashboardProjects,
      allProjects: filteredProjectsData, // All projects for dropdown (not just recent active)
      users: dashboardUsers,
      projectUsers: projectUsersData,
      plannedHoursEntries: plannedHoursData,
      alerts: [], // TODO: Implement alerts system
      recentActivity: [] // TODO: Implement recent activity tracking
    });
  }, [projects, users, projectUsers, tasks, isAdmin, user]);

  // Fetch all data when user is authenticated and token is ready
  useEffect(() => {
    const fetchAllData = async () => {
      // Only fetch data if user is authenticated AND token is ready
      if (!user || !tokenReady) {
        console.log('⏳ Skipping data fetch:', {
          hasUser: !!user,
          tokenReady,
          reason: !user ? 'no user' : 'token not ready'
        });
        setLoading(false);
        return;
      }

      console.log('🔄 Starting data fetch for authenticated user');
      setLoading(true);
      
      try {
        // Fetch projects, users, tasks, and additional data in parallel
        const [projectsRes, usersRes, tasksRes, projectUsersRes, actualHoursRes, plannedHoursRes] = await Promise.all([
          axios.get('/api/projects', {
            headers: { 'Cache-Control': 'no-cache' },
            withCredentials: true
          }),
          axios.get('/api/users', { withCredentials: true }),
          axios.get('/api/tasks', { withCredentials: true }),
          axios.get('/api/project-users', { withCredentials: true }),
          axios.get('/api/actual-hours', { withCredentials: true }),
          axios.get('/api/planned-hours', { withCredentials: true })
        ]);

        console.log('✅ Data fetch completed:', {
          projects: projectsRes.data.length,
          users: usersRes.data.length,
          tasks: tasksRes.data.length
        });

        setProjects(projectsRes.data);
        setUsers(usersRes.data);
        setTasks(tasksRes.data);
        setProjectUsers(projectUsersRes.data);
        setTimesheets(actualHoursRes.data);

        // Calculate dashboard data
        updateDashboardData(
          projectsRes.data,
          usersRes.data,
          projectUsersRes.data,
          tasksRes.data,
          actualHoursRes.data,
          plannedHoursRes.data
        );

        setLoading(false);
        setError(null); // Clear any previous errors
      } catch (err) {
        console.error('❌ Error fetching data:', err);

        // Check if it's an authentication error
        if (err.response?.status === 401) {
          console.log('🔄 Authentication error - user will be redirected to login');
          setError('Authentication required. Redirecting to login...');
          // Don't set loading to false here - let the redirect handle it
        } else if (err.message === 'No authentication token available') {
          console.log('🔄 No token available - user needs to login');
          setError('Please log in to continue');
          setLoading(false);
        } else {
          console.error('❌ Unexpected error:', err.message);
          setError('Failed to load data. Please try again later.');
          setLoading(false);
        }
      }
    };

    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tokenReady]); // Depend on both user and tokenReady


  // Function to add a new project
  const addProject = async (projectData) => {
    try {
      // Ensure job_number is included, remove hourly_rate since it's no longer used
      const projectWithFields = {
        ...projectData,
        job_number: projectData.job_number || ''
      };

      // Remove hourly_rate from the data sent to backend since it's handled automatically
      delete projectWithFields.hourly_rate;

      const response = await axios.post('/api/projects', projectWithFields);
      const newProject = { ...projectWithFields, id: response.data.id };

      // Update projects state
      const updatedProjects = [...projects, newProject];
      setProjects(updatedProjects);

      // Update dashboard data
      updateDashboardData(updatedProjects);

      return newProject;
    } catch (err) {
      console.error('Error adding project:', err);
      throw err;
    }
  };

  // Function to update a project
  const updateProject = async (projectId, projectData) => {
    try {
      // Ensure job_number and item_no are included, remove hourly_rate since it's no longer used
      const projectWithFields = {
        ...projectData,
        job_number: projectData.job_number || '',
        item_no: projectData.item_no || ''
      };

      // Remove hourly_rate from the data sent to backend since it's handled automatically
      delete projectWithFields.hourly_rate;

      await axios.put(`/api/projects/${projectId}`, projectWithFields);

      // Update projects state
      const updatedProjects = projects.map(project =>
        project.id === projectId ? { ...project, ...projectWithFields } : project
      );
      setProjects(updatedProjects);

      // Update dashboard data
      updateDashboardData(updatedProjects);

      return { ...projectWithFields, id: projectId };
    } catch (err) {
      console.error('Error updating project:', err);
      throw err;
    }
  };

  // Function to delete a project
  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`/api/projects/${projectId}`);

      // Update projects state
      const updatedProjects = projects.filter(project => project.id !== projectId);
      setProjects(updatedProjects);

      // Update dashboard data
      updateDashboardData(updatedProjects);

      return true;
    } catch (err) {
      console.error('Error deleting project:', err);
      throw err;
    }
  };

  // Function to add a user to a project
  const assignUserToProject = async (projectId, userId, role, projectLead = false) => {
    try {
      const response = await axios.post('/api/project-users', {
        project_id: projectId,
        user_id: userId,
        role: role || 'trainee_quantity_surveyor',
        project_lead: projectLead
      });

      // Get the project and user details
      const project = projects.find(p => p.id === projectId);
      const user = users.find(u => u.id === userId);

      // Create the new assignment with additional details
      const newAssignment = {
        id: response.data.id,
        project_id: projectId,
        user_id: userId,
        role: role || 'trainee_quantity_surveyor',
        project_name: project?.name,
        user_name: user?.name
      };

      // Update project-users state using functional form to handle rapid updates
      setProjectUsers(prevProjectUsers => {
        const updatedProjectUsers = [...prevProjectUsers, newAssignment];
        // Update dashboard data with the new state
        updateDashboardData(undefined, undefined, updatedProjectUsers);
        return updatedProjectUsers;
      });

      return newAssignment;
    } catch (err) {
      console.error('Error assigning user to project:', err);
      throw err;
    }
  };

  // Function to remove a user from a project
  const removeUserFromProject = async (assignmentId) => {
    try {
      await axios.delete(`/api/project-users/${assignmentId}`);

      // Update project-users state
      const updatedProjectUsers = projectUsers.filter(pu => pu.id !== assignmentId);
      setProjectUsers(updatedProjectUsers);

      // Update dashboard data
      updateDashboardData(undefined, undefined, updatedProjectUsers);

      return true;
    } catch (err) {
      console.error('Error removing user from project:', err);
      throw err;
    }
  };

  // Function to add a new task
  const addTask = async (taskData) => {
    try {
      const response = await axios.post('/api/tasks', taskData);
      const newTask = { ...taskData, id: response.data.id };

      // Update tasks state
      const updatedTasks = [...tasks, newTask];
      setTasks(updatedTasks);

      return newTask;
    } catch (err) {
      console.error('Error adding task:', err);
      throw err;
    }
  };

  // Function to update a task
  const updateTask = async (taskId, taskData) => {
    try {
      await axios.put(`/api/tasks/${taskId}`, taskData);

      // Update tasks state
      const updatedTasks = tasks.map(task =>
        task.id === taskId ? { ...task, ...taskData } : task
      );
      setTasks(updatedTasks);

      return { ...taskData, id: taskId };
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  };

  // Function to delete a task
  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`/api/tasks/${taskId}`);

      // Update tasks state
      const updatedTasks = tasks.filter(task => task.id !== taskId);
      setTasks(updatedTasks);

      return true;
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  };

  // Function to log time (add actual hours)
  const logTime = async (timesheetData) => {
    try {
      const response = await axios.post('/api/actual-hours', timesheetData);
      const newTimesheet = { ...timesheetData, id: response.data.id };

      // Update timesheets state
      const updatedTimesheets = [...timesheets, newTimesheet];
      setTimesheets(updatedTimesheets);

      // Update dashboard data with new hours
      // This is simplified - in a real app, you'd recalculate actual hours
      const updatedDashboardData = {
        ...dashboardData,
        actualHours: dashboardData.actualHours + parseFloat(timesheetData.hours)
      };
      setDashboardData(updatedDashboardData);

      return newTimesheet;
    } catch (err) {
      console.error('Error logging time:', err);
      throw err;
    }
  };

  // Function to update logged time
  const updateLoggedTime = async (timesheetId, timesheetData) => {
    try {
      await axios.put(`/api/actual-hours/${timesheetId}`, timesheetData);

      // Update timesheets state
      const oldTimesheet = timesheets.find(t => t.id === timesheetId);
      const hoursDifference = parseFloat(timesheetData.hours) - parseFloat(oldTimesheet?.hours || 0);

      const updatedTimesheets = timesheets.map(timesheet =>
        timesheet.id === timesheetId ? { ...timesheet, ...timesheetData } : timesheet
      );
      setTimesheets(updatedTimesheets);

      // Update dashboard data with changed hours
      const updatedDashboardData = {
        ...dashboardData,
        actualHours: dashboardData.actualHours + hoursDifference
      };
      setDashboardData(updatedDashboardData);

      return { ...timesheetData, id: timesheetId };
    } catch (err) {
      console.error('Error updating logged time:', err);
      throw err;
    }
  };

  // Function to delete logged time
  const deleteLoggedTime = async (timesheetId) => {
    try {
      await axios.delete(`/api/actual-hours/${timesheetId}`);

      // Find the timesheet to get the hours
      const timesheet = timesheets.find(t => t.id === timesheetId);
      const hours = parseFloat(timesheet?.hours || 0);

      // Update timesheets state
      const updatedTimesheets = timesheets.filter(t => t.id !== timesheetId);
      setTimesheets(updatedTimesheets);

      // Update dashboard data
      const updatedDashboardData = {
        ...dashboardData,
        actualHours: Math.max(0, dashboardData.actualHours - hours)
      };
      setDashboardData(updatedDashboardData);

      return true;
    } catch (err) {
      console.error('Error deleting logged time:', err);
      throw err;
    }
  };

  // Function to get project users
  const getProjectUsers = (projectId) => {
    return projectUsers.filter(pu => pu.project_id === projectId);
  };

  // Function to get user projects
  const getUserProjects = (userId) => {
    const userAssignments = projectUsers.filter(pu => pu.user_id === userId);
    const user = users.find(u => u.id === userId);
    return userAssignments.map(assignment => {
      const project = projects.find(p => p.id === assignment.project_id);
      return {
        ...project,
        role: user?.role || assignment.role, // Use user's actual role, fallback to assignment role
        assignment_id: assignment.id
      };
    });
  };

  // Function to get project tasks
  const getProjectTasks = (projectId) => {
    return tasks.filter(task => task.project_id === projectId);
  };

  // Function to delete a user
  const deleteUser = async (userId) => {
    try {
      console.log('🗑️ Deleting user with ID:', userId);

      // Use the correct Auth0 route for user deletion
      await axios.delete(`/api/auth0/users/${userId}`, { withCredentials: true });

      console.log('✅ User deleted successfully from backend');

      // Update users state
      const updatedUsers = users.filter(user => user.id !== userId);
      setUsers(updatedUsers);

      // Update dashboard data
      updateDashboardData(undefined, updatedUsers);

      return true;
    } catch (err) {
      console.error('❌ Error deleting user:', err);
      console.error('Error details:', err.response?.data);
      throw err;
    }
  };

  // Function to refresh dashboard data (useful after WPS creation/updates)
  const refreshDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Refreshing dashboard data...');

      // Fetch fresh planned hours data
      const plannedHoursResponse = await axios.get('/api/planned-hours', { withCredentials: true });
      const plannedHoursData = plannedHoursResponse.data;

      console.log('🔄 Fetched fresh planned hours:', plannedHoursData.length, 'entries');

      // Update dashboard data with fresh planned hours
      updateDashboardData(
        projects,
        users,
        projectUsers,
        tasks,
        timesheets, // actual hours
        plannedHoursData // fresh planned hours
      );

      setLoading(false);
      return plannedHoursData;
    } catch (err) {
      console.error('Error refreshing dashboard data:', err);
      setError('Failed to refresh dashboard data');
      setLoading(false);
      throw err;
    }
  };

  // Value object to be provided to consumers
  const contextValue = {
    // State
    projects,
    users,
    tasks,
    projectUsers,
    timesheets,
    dashboardData,
    loading,
    error,

    // Project functions
    addProject,
    updateProject,
    deleteProject,

    // User functions
    deleteUser,

    // User-Project assignment functions
    assignUserToProject,
    removeUserFromProject,
    getProjectUsers,
    getUserProjects,

    // Task functions
    addTask,
    updateTask,
    deleteTask,
    getProjectTasks,

    // Timesheet functions
    logTime,
    updateLoggedTime,
    deleteLoggedTime,

    // Dashboard functions
    refreshDashboardData
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;
